import React, { useState, useMemo } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Badge,
  Button,
  Dialog,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { buildSections } from './buildSections';
import { ProfileSubnavProps, SectionKey } from './types';

/**
 * Mobile-optimized profile navigation
 *
 * Shows core tabs (priority 0-3) in compact tab bar
 * Additional tabs accessible via bottom sheet dialog
 */
export const ProfileSubnavMobile: React.FC<
  Omit<ProfileSubnavProps, 'sticky' | 'variant'>
> = ({
  personId,
  activeKey,
  counts = {},
  flags = {},
  perms = {},
  onNavigate,
  routePrefix,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  // Build all sections
  const allSections = useMemo(
    () => buildSections(personId, counts, flags, perms, routePrefix),
    [personId, counts, flags, perms, routePrefix]
  );

  // Split into core (priority 0-3) and overflow (priority 4+)
  const coreSections = allSections.filter(s => s.priority <= 3);
  const overflowSections = allSections.filter(s => s.priority > 3);

  // Handle navigation
  const handleOverflowClick = (key: SectionKey) => {
    setDialogOpen(false);
    onNavigate(key);
  };

  // Handle tab change from Tabs component
  const handleTabChange = (_: React.SyntheticEvent, newValue: SectionKey) => {
    onNavigate(newValue);
  };

  return (
    <>
      <Box
        role="navigation"
        aria-label="Profile sections"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: 'background.paper',
        }}
      >
        {/* Core Tabs */}
        <Tabs
          value={activeKey}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            flex: 1,
            minWidth: 0,
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          }}
          aria-label="Primary profile sections"
        >
          {coreSections.map((section) => (
            <Tab
              key={section.key}
              value={section.key}
              aria-label={section.ariaLabel ?? section.label}
              aria-selected={section.key === activeKey}
              label={
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  {section.badgeCount !== undefined && section.badgeCount > 0 ? (
                    <Badge badgeContent={section.badgeCount} color="primary" max={99}>
                      {section.icon}
                    </Badge>
                  ) : (
                    section.icon
                  )}
                  <Box
                    component="span"
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 500,
                    }}
                  >
                    {section.label}
                  </Box>
                </Box>
              }
              sx={{
                textTransform: 'none',
                minHeight: 72,
                minWidth: 80,
                px: 1,
                '&.Mui-selected': {
                  color: 'primary.main',
                },
              }}
            />
          ))}
        </Tabs>

        {/* "More" Button */}
        {overflowSections.length > 0 && (
          <Button
            onClick={() => setDialogOpen(true)}
            aria-label="More sections"
            size="small"
            sx={{
              mr: 1,
              minWidth: 60,
              textTransform: 'none',
            }}
          >
            More
          </Button>
        )}
      </Box>

      {/* Bottom Sheet Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="more-sections-title"
        PaperProps={{
          sx: {
            position: 'fixed',
            bottom: 0,
            m: 0,
            maxHeight: '60vh',
            borderRadius: '16px 16px 0 0',
          },
        }}
      >
        <DialogTitle id="more-sections-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          More Sections
          <IconButton onClick={() => setDialogOpen(false)} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <List sx={{ pt: 0 }}>
          {overflowSections.map((section) => (
            <ListItemButton
              key={section.key}
              onClick={() => handleOverflowClick(section.key)}
              selected={section.key === activeKey}
            >
              {section.icon && <ListItemIcon>{section.icon}</ListItemIcon>}
              <ListItemText primary={section.label} />
              {section.badgeCount !== undefined && section.badgeCount > 0 && (
                <Badge
                  badgeContent={section.badgeCount}
                  color="primary"
                  max={99}
                />
              )}
            </ListItemButton>
          ))}
        </List>
      </Dialog>
    </>
  );
};

export default ProfileSubnavMobile;
